import React, { useState, useEffect } from 'react';
import {
  Grid, Paper, Typography, Box, Card, CardContent, Button, Avatar, Chip, LinearProgress, CircularProgress, Backdrop
} from '@mui/material';
import {
  CalendarToday, Star, LocalOffer, AccountCircle, Add, History
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clientData, setClientData] = useState({
    loyaltyPoints: 0,
    membershipType: 'Basic',
    totalSpent: 0,
    totalAppointments: 0,
    recentAppointments: [],
    upcomingAppointments: [],
    popularServices: [] as any[]
  });
  const [loyaltySettings, setLoyaltySettings] = useState({ enabled: false });
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    fetchClientStats();
    fetchLoyaltySettings();
  }, [user]);

  const fetchLoyaltySettings = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/loyalty/settings');
      if (response.ok) {
        const settings = await response.json();
        setLoyaltySettings(settings);
      }
    } catch (error) {
      console.error('Error fetching loyalty settings:', error);
    }
  };

  const fetchClientStats = async () => {
    if (!user?.email) return;
    
    setDataLoading(true);
    try {
      // Fetch appointments for the client
      const response = await fetch('https://salon.rockyroadsolutions.com/api/appointments');
      if (response.ok) {
        const appointments = await response.json();
        
        // Filter appointments for current client
        const clientAppointments = appointments.filter((apt: any) => 
          apt.client?.email === user.email || apt.client?.id === user.id
        );
        
        // Calculate stats
        const totalSpent = clientAppointments.reduce((sum: number, apt: any) => 
          sum + parseFloat(apt.totalPrice) + parseFloat(apt.tipAmount || 0), 0
        );
        
        const completedAppointments = clientAppointments.filter((apt: any) => 
          apt.status.toUpperCase() === 'COMPLETED'
        );
        
        const upcomingAppointments = clientAppointments.filter((apt: any) => 
          apt.status.toUpperCase() === 'SCHEDULED' || apt.status.toUpperCase() === 'CONFIRMED'
        );
        
        const recentAppointments = completedAppointments
          .sort((a: any, b: any) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime())
          .slice(0, 3);
        
        console.log('Client appointment statuses:', clientAppointments.map((apt: any) => apt.status));
        console.log('Completed appointments:', completedAppointments.length);
        console.log('Recent appointments:', recentAppointments);
        
        // Calculate loyalty points (1 point per dollar spent)
        const loyaltyPoints = Math.floor(totalSpent);
        
        // Determine membership type based on total spent
        let membershipType = 'Basic';
        if (totalSpent >= 500) membershipType = 'VIP';
        else if (totalSpent >= 200) membershipType = 'Premium';
        
        // Get popular services (using static data since we don't have service details in appointments)
        const popularServices = [
          { name: 'Hair Cutting', price: 65, duration: '60 min', bookings: Math.floor(Math.random() * 50) + 20 },
          { name: 'Manicure', price: 35, duration: '45 min', bookings: Math.floor(Math.random() * 40) + 15 },
          { name: 'Facial Treatment', price: 85, duration: '75 min', bookings: Math.floor(Math.random() * 30) + 10 }
        ].sort((a, b) => b.bookings - a.bookings);
        
        setClientData({
          loyaltyPoints,
          membershipType,
          totalSpent,
          totalAppointments: clientAppointments.length,
          recentAppointments,
          upcomingAppointments,
          popularServices
        });
      }
    } catch (error) {
      console.error('Error fetching client stats:', error);
    } finally {
      setLoading(false);
      setDataLoading(false);
    }
  };

  const membershipBenefits = {
    Basic: { discount: 10, color: '#FF9800' },
    Premium: { discount: 15, color: '#9C27B0' },
    VIP: { discount: 20, color: '#E91E63' }
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box
      sx={{
        background: `
          linear-gradient(135deg, rgba(255, 248, 245, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%),
          url('https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=1974&q=80')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      {/* Welcome Header */}
      <Card sx={{ mb: 4, background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                fontSize: '2rem'
              }}
            >
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h4" fontWeight={600} gutterBottom>
                Welcome back, {user?.firstName}! ✨
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Ready for your next beauty session?
              </Typography>
              {loyaltySettings.enabled && (
                <Box display="flex" gap={2} mt={2}>
                  <Chip
                    label={`${clientData.membershipType} Member`}
                    sx={{
                      backgroundColor: membershipBenefits[clientData.membershipType as keyof typeof membershipBenefits].color,
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                  <Chip
                    label={`${clientData.loyaltyPoints} Points`}
                    icon={<Star />}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => navigate('/book-appointment')}
              sx={{ px: 4, py: 2 }}
            >
              Book Appointment
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Loyalty Progress */}
        {loyaltySettings.enabled && (
          <Grid item xs={12} md={6}>
            <Card sx={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Star sx={{ color: '#FFD700' }} />
                  <Typography variant="h6" fontWeight={600}>
                    Loyalty Rewards
                  </Typography>
                </Box>
                <Typography variant="h3" color="primary" fontWeight={700} gutterBottom>
                  {clientData.loyaltyPoints}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Points earned • Total spent: ${clientData.totalSpent.toFixed(2)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((clientData.loyaltyPoints / 200) * 100, 100)}
                  sx={{ height: 8, borderRadius: 4, mt: 2 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {clientData.loyaltyPoints >= 200 ? 'Eligible for free service!' : `${200 - clientData.loyaltyPoints} points to next free service`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Membership Benefits */}
        {loyaltySettings.enabled && (
          <Grid item xs={12} md={6}>
            <Card sx={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <LocalOffer sx={{ color: membershipBenefits[clientData.membershipType as keyof typeof membershipBenefits].color }} />
                  <Typography variant="h6" fontWeight={600}>
                    {clientData.membershipType} Benefits
                  </Typography>
                </Box>
                <Typography variant="h3" color="primary" fontWeight={700} gutterBottom>
                  {membershipBenefits[clientData.membershipType as keyof typeof membershipBenefits].discount}%
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Discount on all services
                </Typography>
                <Box mt={2}>
                  <Typography variant="body2" sx={{ mb: 1 }}>📊 Total Appointments: {clientData.totalAppointments}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>📅 Upcoming: {clientData.upcomingAppointments.length}</Typography>
                  <Typography variant="body2">✨ Priority booking</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Stats Card - Always visible */}
        {!loyaltySettings.enabled && (
          <Grid item xs={12} md={6}>
            <Card sx={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <AccountCircle color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Your Stats
                  </Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="body2" sx={{ mb: 1 }}>📊 Total Appointments: {clientData.totalAppointments}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>📅 Upcoming: {clientData.upcomingAppointments.length}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>💰 Total Spent: ${clientData.totalSpent.toFixed(2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Activity */}
        <Grid item xs={12} md={loyaltySettings.enabled ? 4 : 6}>
          <Card sx={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <History color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Recent Visits
                </Typography>
              </Box>
              <Box>
                {clientData.recentAppointments.length > 0 ? clientData.recentAppointments.map((appointment: any, index: number) => (
                  <Box key={index} sx={{ py: 2, borderBottom: index < clientData.recentAppointments.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <Typography variant="body1" fontWeight={500}>
                      ${(parseFloat(appointment.totalPrice) + parseFloat(appointment.tipAmount || 0)).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      with {appointment.primaryStaff?.firstName}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(appointment.appointmentDate).toLocaleDateString()}
                      </Typography>
                      <Chip label={appointment.status} size="small" color="success" />
                    </Box>
                  </Box>
                )) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No recent appointments
                  </Typography>
                )}
              </Box>
              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 2 }}
                onClick={() => navigate('/appointments')}
              >
                View All Appointments
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Popular Services - Moved to last */}
        <Grid item xs={12}>
          <Card sx={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Popular Services
              </Typography>
              <Grid container spacing={2}>
                {clientData.popularServices.map((service: any, index: number) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(233, 30, 99, 0.2)' }
                      }}
                      onClick={() => navigate('/book-appointment')}
                    >
                      <Box
                        sx={{
                          height: 120,
                          background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.8) 0%, rgba(173, 20, 87, 0.8) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="h6" color="white" fontWeight={600} textAlign="center">
                          {service.name}
                        </Typography>
                      </Box>
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h6" color="primary" fontWeight={600}>
                          ${service.price}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {service.duration} • {service.bookings} bookings
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientDashboard;
